import { Fragment, type ReactNode } from "react";

/**
 * Stats' two designed arrangements.
 *
 * **The breakpoint moved to `lib/useWideLayout.ts`** when Review's answer face became the second
 * surface to need it (日本-350). Re-exported here so `Recall` keeps importing its layout pieces
 * from one place, and so the argument for resolving it in JS — the two arrangements are different
 * *partitions* of the same panels, which no flex property turns into one another — stays beside the
 * components that rely on it.
 */
export { useWideLayout } from "@/lib/useWideLayout";

/**
 * One horizontal band of the 16:9 frames — a row of cards that share a height.
 *
 * **Weights are the frame's own pixel widths**, used as flex ratios rather than as sizes, so the
 * proportions survive the surface being wider than 1920 (⅔ of Alex's monitor is ~2293) without a
 * second set of numbers to keep in step. `flex-basis: 0` is what makes them ratios: with the default
 * `auto` the content width leaks in and a table-heavy panel quietly claims more than its share.
 *
 * Cards in a band **do** stretch to the tallest, and that is the design rather than an oversight —
 * a band is assembled from panels of comparable weight, so aligned bottoms read as a strip. It is
 * pairing a tall panel with a short one that wastes the space, which is what the columns below are
 * for.
 */
export function Band({ items }: { items: [number, ReactNode][] }) {
  const shown = items.filter(([, node]) => node);
  if (shown.length === 0) return null;
  return (
    <div className="flex gap-[14px]">
      {/* `[&>*]:h-full` is what makes the band a band. The wrapper already stretches to the row's
          height — that is flexbox's default — but the panel inside it does not, so without this the
          cards sit at their content heights with ragged bottoms and the row stops reading as one
          thing. The frames draw every card in a band at an identical height. */}
      {shown.map(([weight, node], index) => (
        <div key={index} className="min-w-0 [&>*]:h-full" style={{ flex: `${weight} 1 0%` }}>
          {node}
        </div>
      ))}
    </div>
  );
}

/**
 * The 1147 arrangement: a wide column beside a 352px one, each flowing on its own.
 *
 * **Every card snugs up to the card above it in its own column** (Alex, 8 Aug 2026). Built as rows
 * instead — which is how the frames draw it, because their mock content happens to make each pair
 * equal — a short panel beside a tall one leaves the difference as dead space, and the bottom of
 * Recall was mostly that: `Stability` is four bars and `Answer buttons` is four labelled stacks, so
 * the row was the taller of the two and the left column ended in a canyon.
 *
 * Below `lg` the columns collapse and everything stacks in DOM order — the wide column's panels,
 * then the narrow one's. That reorders the phone against the frame, and it is the honest trade: the
 * alternative is a third arrangement to maintain for a width the frames do not draw, and the
 * grouping still holds (the pair that belongs together is still adjacent).
 */
export function Columns({ wide, narrow }: { wide: ReactNode[]; narrow: ReactNode[] }) {
  return (
    <div className="flex flex-col gap-[14px] lg:flex-row lg:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-[14px]">
        {wide.map((panel, index) => (
          <Fragment key={index}>{panel}</Fragment>
        ))}
      </div>
      <div className="flex shrink-0 flex-col gap-[14px] lg:w-[352px]">
        {narrow.map((panel, index) => (
          <Fragment key={index}>{panel}</Fragment>
        ))}
      </div>
    </div>
  );
}
